import { useState } from "react";
import { motion } from "framer-motion";
import { X, Flag, AlertTriangle } from "lucide-react";
import { api } from "../contexts/AuthContext";
import AvatarDisplay from "./AvatarDisplay";

const REASONS = [
  { value: "harassment",           label: "Harassment or bullying" },
  { value: "hate_content",         label: "Hate or abusive content" },
  { value: "sexual_inappropriate", label: "Sexual or inappropriate content" },
  { value: "threats",              label: "Threats", high: true },
  { value: "spam",                 label: "Spam" },
  { value: "scamming",             label: "Scamming" },
  { value: "other",                label: "Other" },
];

/**
 * Reusable report modal.
 * type = "message" | "user"
 * target = { user_id, alias, avatar_id, msg_id?, room_id?, message_content? }
 */
export default function ReportModal({ type, target, onClose }) {
  const [reason, setReason] = useState("");
  const [explanation, setExplanation] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async () => {
    if (!reason) return;
    setLoading(true);
    setError("");
    try {
      if (type === "message") {
        await api.post("/reports/message", {
          reported_user_id: target.user_id,
          msg_id: target.msg_id,
          room_id: target.room_id || "",
          reason,
          explanation,
          message_content: target.message_content || ""
        });
      } else {
        await api.post("/reports/user", {
          reported_user_id: target.user_id,
          room_id: target.room_id || "",
          reason,
          explanation
        });
      }
      setDone(true);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to submit report. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}>
      <motion.div initial={{ opacity: 0, scale: 0.95, y: 8 }} animate={{ opacity: 1, scale: 1, y: 0 }}
        className="bg-[#0E0E17] border border-red-500/25 rounded-2xl w-full max-w-sm shadow-2xl"
        onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-red-500/15">
          <div className="flex items-center gap-2">
            <Flag className="w-4 h-4 text-red-400" />
            <h2 className="font-bold text-white font-[Outfit]">
              {type === "message" ? "Report Message" : "Report User"}
            </h2>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5">
          {done ? (
            <div className="text-center py-6 space-y-3">
              <div className="w-12 h-12 mx-auto rounded-full bg-emerald-500/20 border border-emerald-500/30 flex items-center justify-center">
                <Flag className="w-5 h-5 text-emerald-400" />
              </div>
              <p className="font-semibold text-white">Report submitted</p>
              <p className="text-sm text-slate-400 leading-relaxed">
                Thank you. Our moderation team will review this privately.
                The reporter's identity is never revealed.
              </p>
              <button onClick={onClose}
                className="mt-2 px-6 py-2 bg-gradient-to-r from-violet-600 to-cyan-500 text-white text-sm font-semibold rounded-xl hover:opacity-90">
                Done
              </button>
            </div>
          ) : (
            <>
              {/* Target info */}
              <div className="flex items-center gap-2.5 mb-4 p-3 bg-[#141322] rounded-xl">
                <AvatarDisplay avatarId={target.avatar_id} size="sm" />
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-white truncate">{target.alias}</p>
                  {type === "message" && target.message_content && (
                    <p className="text-xs text-slate-400 truncate mt-0.5">
                      "{target.message_content}"
                    </p>
                  )}
                </div>
              </div>

              {/* Reason selection */}
              <div className="space-y-1.5 mb-4">
                {REASONS.map(r => (
                  <label key={r.value}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-xl border cursor-pointer transition-all ${
                      reason === r.value
                        ? "border-red-500/50 bg-red-500/10"
                        : "border-slate-700 hover:border-slate-600"
                    }`}>
                    <input type="radio" name="reason" value={r.value}
                      checked={reason === r.value}
                      onChange={() => setReason(r.value)}
                      className="accent-red-500 shrink-0" />
                    <span className="text-sm text-slate-200 flex-1">{r.label}</span>
                    {r.high && (
                      <span className="flex items-center gap-1 text-[10px] font-mono text-amber-400">
                        <AlertTriangle className="w-3 h-3" /> PRIORITY
                      </span>
                    )}
                  </label>
                ))}
              </div>

              {/* Optional explanation */}
              <textarea
                data-testid="report-explanation"
                value={explanation}
                onChange={e => setExplanation(e.target.value)}
                placeholder="Additional context (optional)"
                rows={2}
                maxLength={500}
                className="w-full bg-[#141322] border border-slate-700 rounded-xl py-2.5 px-3 text-white text-sm placeholder-slate-600 focus:outline-none focus:border-red-500/50 resize-none transition-colors mb-3"
              />

              {error && <p className="text-red-400 text-xs mb-3">{error}</p>}

              <p className="text-[11px] text-slate-500 mb-3 leading-relaxed">
                Reports are reviewed privately. The reported user will not know who submitted this.
              </p>

              <div className="flex gap-2">
                <button onClick={onClose}
                  className="flex-1 py-2.5 border border-slate-700 text-slate-300 rounded-xl text-sm hover:border-slate-500 transition-colors">
                  Cancel
                </button>
                <button
                  data-testid="submit-report-btn"
                  onClick={handleSubmit}
                  disabled={!reason || loading}
                  className="flex-1 py-2.5 bg-red-500/80 hover:bg-red-500 text-white font-semibold rounded-xl text-sm transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {loading ? "Submitting..." : "Submit Report"}
                </button>
              </div>
            </>
          )}
        </div>
      </motion.div>
    </div>
  );
}
